import { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  Box,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useBackdrop } from '../../common/components/backDrop_context.component';
import { formUploadRanDb } from './functions/form_upload_ran_db.function';
import { notify } from '../../common/functions/react-toastify.function';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectedRanDbRow, openUploadFormRanDb } from '../../common/atom/atoms';

const defaultValues = {
  site_code: '',
  physical: '',
  locked_status: '',
  vendor: '',
  region: '',
  coverage_type: '',
  lat: null,
  long: null,
  site_status: '',
  pta_tehsil: '',
  pta_district: '',
  pta_province: '',
  prefix: '',
  city_prefix_based: '',
  technical_city: '',
  commercial_district: '',
  commercial_city: '',
  mbu: '',
  franchise_id: null,
  site_type: '',
  sharing_type_hostguest: '',
  omo_name: '',
  b2s_id: '',
  bsc: '',
  bsc_code: '',
  lte_technology: '',
  deodar_tag: '',
  // Commented out fields - keeping for future reference
  // '2g': '',
  // '4g': '',
  // part_of_dec_cfo: '',
  // scope: '',
  // pta_remarks: '',
  // usf_site: '',
  // commercial_subregion: '',
  // commercial_region: '',
  // '4g_capacity': '',
  // '2g_locked_tag': '',
  // '4g_locked_tag': '',
  // '2g_cells': null,
  // '4g_cells': null,
  // l1800a_cells: null,
  // l1800b_cells: null,
  // mmcells: null,
  // total_l18_ab_cells: null,
  // l2100_cells: null,
  // l900a_cells: null,
  // total_trx: null,
  // '900_trx': null,
  // '1800_trx': null,
  // onfp_remarks: '',
  // bp_remarks: '',
  // comments: '',
};

const options = {
  physical: ['2G', '3G', '4G', '2G+4G'],
  locked_status: ['Locked', 'Temporary Locked', 'Unlocked', 'Inactive'],
  vendor: ['Huawei', 'Ericsson', 'ZTE'],
  region: ['North', 'South', 'Central A', 'Central B'],
  coverage_type: ['Urban', 'Rural', 'Macro'],
  site_status: ['Active', 'Inactive'],
  pta_tehsil: ['LIAQUATPUR TEHSIL', 'Tehsil A', 'Tehsil B'],
  pta_district: ['RAHIM YAR KHAN', 'District X', 'District Y'],
  pta_province: ['PUNJAB', 'Province A', 'Province B'],
  prefix: ['ABB', 'Prefix A', 'Prefix B'],
  city_prefix_based: ['Abbasia', 'City A', 'City B'],
  technical_city: ['Rural', 'Urban', 'Tech City A'],
  commercial_district: ['Rahim Yar Khan', 'District A', 'District B'],
  commercial_city: ['Liaqatpur_Rural', 'City A', 'City B'],
  mbu: ['C7-KNP-06', 'MBU 1', 'MBU 2'],
  site_type: ['Tower co B2S', 'Type A', 'Type B'],
  sharing_type_hostguest: ['Host', 'Guest'],
  omo_name: ['Enfrashare', 'OMO A', 'OMO B'],
  b2s_id: ['ES2-LQP-01977', 'B2S-001', 'B2S-002'],
  bsc: ['RYK317Z', 'BSC A', 'BSC B'],
  bsc_code: ['RYK317Z_MGW_2', 'CODE-001', 'CODE-002'],
  lte_technology: ['LTE', 'LTE-A', 'LTE-Pro', '5G'],
  deodar_tag: ['Tag A', 'Tag B', 'Tag C'],
  // Commented out options - keeping for future reference
  // '2g': ['2G', '3G', '4G'],
  // '4g': ['2G', '3G', '4G'],
  // part_of_dec_cfo: ['Yes', 'No'],
  // scope: ['Scope A', 'Scope B'],
  // pta_remarks: ['Approved', 'Rejected'],
  // usf_site: ['Yes', 'No'],
  // commercial_subregion: ['Subregion 1', 'Subregion 2'],
  // commercial_region: ['Region 1', 'Region 2'],
  // '4g_capacity': ['Capacity A', 'Capacity B'],
  // '2g_locked_tag': ['Tag A', 'Tag B'],
  // '4g_locked_tag': ['Tag A', 'Tag B'],
};

const fieldLabels = {
  physical: 'Physical',
  locked_status: 'Locked Status',
  vendor: 'Vendor',
  region: 'Region',
  coverage_type: 'Coverage Type',
  site_status: 'Site Status',
  pta_tehsil: 'PTA Tehsil',
  pta_district: 'PTA District',
  pta_province: 'PTA Province',
  prefix: 'Prefix',
  city_prefix_based: 'City (prefix based)',
  technical_city: 'Technical City',
  commercial_district: 'Commercial District',
  commercial_city: 'Commercial City',
  mbu: 'MBU',
  franchise_id: 'Franchise ID',
  site_type: 'Site Type',
  sharing_type_hostguest: 'Sharing Type (Host/Guest)',
  omo_name: 'OMO Name',
  b2s_id: 'B2S ID',
  bsc: 'BSC',
  bsc_code: 'BSC Code',
  lte_technology: 'LTE Technology',
  deodar_tag: 'Deodar Tag',
  // Commented out labels - keeping for future reference
  // '2g': '2G',
  // '4g': '4G',
  // part_of_dec_cfo: 'Part of DEC CFO',
  // scope: 'Scope',
  // pta_remarks: 'PTA Remarks',
  // usf_site: 'USF Site',
  // commercial_subregion: 'Commercial Subregion',
  // commercial_region: 'Commercial Region',
  // '4g_capacity': '4G Capacity',
  // '2g_locked_tag': '2G Locked Tag',
  // '4g_locked_tag': '4G Locked Tag',
};

const RanDBUploadFormComponent = () => {
  const [formState, setFormState] = useState(defaultValues);
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const [errorState, setErrorState] = useState({});
  const selectedRow = useAtomValue(selectedRanDbRow);
  const setSelectedRow = useSetAtom(selectedRanDbRow);
  const setOpenUploadForm = useSetAtom(openUploadFormRanDb);

  // Populate form with selected row data when editing
  useEffect(() => {
    if (selectedRow) {
      const mappedData = {
        site_code: selectedRow.site_code || '',
        physical: selectedRow.physical || '',
        locked_status: selectedRow.locked_status || '',
        vendor: selectedRow.vendor || '',
        region: selectedRow.region || '',
        coverage_type: selectedRow.coverage_type || '',
        lat: selectedRow.lat || null,
        long: selectedRow.long || null,
        site_status: selectedRow.site_status || '',
        pta_tehsil: selectedRow.pta_tehsil || '',
        pta_district: selectedRow.pta_district || '',
        pta_province: selectedRow.pta_province || '',
        prefix: selectedRow.prefix || '',
        city_prefix_based: selectedRow.city_prefix_based || '',
        technical_city: selectedRow.technical_city || '',
        commercial_district: selectedRow.commercial_district || '',
        commercial_city: selectedRow.commercial_city || '',
        mbu: selectedRow.mbu || '',
        franchise_id: selectedRow.franchise_id || null,
        site_type: selectedRow.site_type || '',
        sharing_type_hostguest: selectedRow.sharing_type_hostguest || '',
        omo_name: selectedRow.omo_name || '',
        b2s_id: selectedRow.b2s_id || '',
        bsc: selectedRow.bsc || '',
        bsc_code: selectedRow.bsc_code || '',
        lte_technology: selectedRow.lte_technology || '',
        deodar_tag: selectedRow.deodar_tag || '',
        // Commented out fields - keeping for future reference
        // '2g': selectedRow['2g'] || '',
        // '4g': selectedRow['4g'] || '',
        // part_of_dec_cfo: selectedRow.part_of_dec_cfo || '',
        // scope: selectedRow.scope || '',
        // pta_remarks: selectedRow.pta_remarks || '',
        // usf_site: selectedRow.usf_site || '',
        // commercial_subregion: selectedRow.commercial_subregion || '',
        // commercial_region: selectedRow.commercial_region || '',
        // '4g_capacity': selectedRow['4g_capacity'] || '',
        // '2g_locked_tag': selectedRow['2g_locked_tag'] || '',
        // '4g_locked_tag': selectedRow['4g_locked_tag'] || '',
        // '2g_cells': selectedRow['2g_cells'] || null,
        // '4g_cells': selectedRow['4g_cells'] || null,
        // l1800a_cells: selectedRow.l1800a_cells || null,
        // l1800b_cells: selectedRow.l1800b_cells || null,
        // mmcells: selectedRow.mmcells || null,
        // total_l18_ab_cells: selectedRow.total_l18_ab_cells || null,
        // l2100_cells: selectedRow.l2100_cells || null,
        // l900a_cells: selectedRow.l900a_cells || null,
        // total_trx: selectedRow.total_trx || null,
        // '900_trx': selectedRow['900_trx'] || null,
        // '1800_trx': selectedRow['1800_trx'] || null,
        // onfp_remarks: selectedRow.onfp_remarks || '',
        // bp_remarks: selectedRow.bp_remarks || '',
        // comments: selectedRow.comments || '',
      };
      setFormState(mappedData);
    }
  }, [selectedRow]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };
  const validateForm = () => {
    const requiredFields = [
      'site_code',
      'physical',
      'locked_status',
      'vendor',
      'region',
      'coverage_type',
      'lat',
      'long',
      'site_status',
      'pta_tehsil',
      'pta_district',
      'pta_province',
      'prefix',
      'city_prefix_based',
      'technical_city',
      'commercial_district',
      'commercial_city',
      'mbu',
      'franchise_id',
      'site_type',
      'sharing_type_hostguest',
      'omo_name',
      'b2s_id',
      'bsc',
      'bsc_code',
      'lte_technology',
      'deodar_tag',
    ];
    const newErrorState = {};

    requiredFields.forEach((field) => {
      if (!formState[field] || formState[field].toString().trim() === '') {
        newErrorState[field] = true;
      }
    });

    setErrorState(newErrorState);

    if (Object.keys(newErrorState).length > 0) {
      notify('error', 'Please fill in all required fields.');
      return false;
    }

    return true;
  };

  const handleAutocompleteChange = (name, value) => {
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleClear = () => {
    setFormState(defaultValues);
    setErrorState({});
    setSelectedRow(null);
  };

  const getFieldStyle = (fieldName) => {
    // Special styling for locked status field
    if (fieldName === 'locked_status') {
      return {
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#f3e5f5',
          border: '2px solid #9c27b0',
          borderRadius: '8px',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: '#f8f4fc',
            border: '2px solid #7b1fa2',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(156, 39, 176, 0.3)',
          },
          '&.Mui-focused': {
            backgroundColor: '#f8f4fc',
            border: '2px solid #7b1fa2',
            boxShadow: '0 0 0 3px rgba(156, 39, 176, 0.3)',
            transform: 'translateY(-1px)',
          },
        },
        '& .MuiInputLabel-root': {
          color: '#7b1fa2',
          fontWeight: 700,
          fontSize: '0.875rem',
          '&.Mui-focused': {
            color: '#7b1fa2',
            fontWeight: 700,
          },
        },
        '& .MuiSelect-select': {
          color: '#4a148c',
          fontWeight: 600,
          fontSize: '0.875rem',
        },
      };
    }

    return {};
  };

  const handleSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    } // Stop submission if validation fails

    try {
      showBackdrop();
      const resp = await formUploadRanDb(formState, selectedRow?.id);
      if (resp?.response) {
        notify('success', 'Saved Successfully');
        // Clear selected row and close form on successful submission
        setSelectedRow(null);
        setOpenUploadForm(false);
      }
      hideBackdrop();
    } catch (error) {
      hideBackdrop();
      console.log('eee', error);
      notify('error', error.message);
    }
  };

  return (
    <>
      <div
        style={{
          width: '100%',
          height: '100%',
          background: 'white',
          boxShadow: '0px 0px 30px 2px rgba(0, 0, 0, 0.05)',
          borderRadius: 8,
          padding: 24,
          marginTop: '1%',
        }}
      >
        <Grid container spacing={2}>
          {/* Site Code */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Site Code"
              name="site_code"
              value={formState.site_code}
              onChange={handleChange}
              size="small"
              error={!!errorState.site_code}
              helperText={errorState.site_code ? 'Required' : ''}
            />
          </Grid>

          {/* Physical */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.physical}
              value={formState.physical || ''}
              onChange={(e, value) => handleAutocompleteChange('physical', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Physical"
                  fullWidth
                  error={!!errorState.physical}
                  helperText={errorState.physical ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* Locked Status - Special colorful field */}
          <Grid item xs={4}>
            <FormControl fullWidth size="small" sx={getFieldStyle('locked_status')}>
              <InputLabel>Locked Status</InputLabel>
              <Select
                value={formState.locked_status || ''}
                onChange={handleChange}
                name="locked_status"
                label="Locked Status"
                error={!!errorState.locked_status}
              >
                {options.locked_status.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Vendor */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.vendor}
              value={formState.vendor || ''}
              onChange={(e, value) => handleAutocompleteChange('vendor', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendor"
                  fullWidth
                  error={!!errorState.vendor}
                  helperText={errorState.vendor ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* Region */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.region}
              value={formState.region || ''}
              onChange={(e, value) => handleAutocompleteChange('region', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Region"
                  fullWidth
                  error={!!errorState.region}
                  helperText={errorState.region ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* Coverage Type */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.coverage_type}
              value={formState.coverage_type || ''}
              onChange={(e, value) => handleAutocompleteChange('coverage_type', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Coverage Type"
                  fullWidth
                  error={!!errorState.coverage_type}
                  helperText={errorState.coverage_type ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* Latitude */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Lat"
              name="lat"
              type="number"
              value={formState.lat || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.lat}
              helperText={errorState.lat ? 'Required' : ''}
            />
          </Grid>

          {/* Longitude */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Long"
              name="long"
              type="number"
              value={formState.long || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.long}
              helperText={errorState.long ? 'Required' : ''}
            />
          </Grid>

          {/* Site Status */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.site_status}
              value={formState.site_status || ''}
              onChange={(e, value) => handleAutocompleteChange('site_status', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Site Status"
                  fullWidth
                  error={!!errorState.site_status}
                  helperText={errorState.site_status ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* PTA Tehsil */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.pta_tehsil}
              value={formState.pta_tehsil || ''}
              onChange={(e, value) => handleAutocompleteChange('pta_tehsil', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="PTA Tehsil"
                  fullWidth
                  error={!!errorState.pta_tehsil}
                  helperText={errorState.pta_tehsil ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* PTA District */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.pta_district}
              value={formState.pta_district || ''}
              onChange={(e, value) => handleAutocompleteChange('pta_district', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="PTA District"
                  fullWidth
                  error={!!errorState.pta_district}
                  helperText={errorState.pta_district ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* PTA Province */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.pta_province}
              value={formState.pta_province || ''}
              onChange={(e, value) => handleAutocompleteChange('pta_province', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="PTA Province"
                  fullWidth
                  error={!!errorState.pta_province}
                  helperText={errorState.pta_province ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* Prefix */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.prefix}
              value={formState.prefix || ''}
              onChange={(e, value) => handleAutocompleteChange('prefix', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Prefix"
                  fullWidth
                  error={!!errorState.prefix}
                  helperText={errorState.prefix ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* City (prefix based) */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.city_prefix_based}
              value={formState.city_prefix_based || ''}
              onChange={(e, value) => handleAutocompleteChange('city_prefix_based', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City (prefix based)"
                  fullWidth
                  error={!!errorState.city_prefix_based}
                  helperText={errorState.city_prefix_based ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* Technical City */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.technical_city}
              value={formState.technical_city || ''}
              onChange={(e, value) => handleAutocompleteChange('technical_city', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Technical City"
                  fullWidth
                  error={!!errorState.technical_city}
                  helperText={errorState.technical_city ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* Commercial District */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.commercial_district}
              value={formState.commercial_district || ''}
              onChange={(e, value) => handleAutocompleteChange('commercial_district', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Commercial District"
                  fullWidth
                  error={!!errorState.commercial_district}
                  helperText={errorState.commercial_district ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* Commercial City */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.commercial_city}
              value={formState.commercial_city || ''}
              onChange={(e, value) => handleAutocompleteChange('commercial_city', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Commercial City"
                  fullWidth
                  error={!!errorState.commercial_city}
                  helperText={errorState.commercial_city ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* MBU */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.mbu}
              value={formState.mbu || ''}
              onChange={(e, value) => handleAutocompleteChange('mbu', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="MBU"
                  fullWidth
                  error={!!errorState.mbu}
                  helperText={errorState.mbu ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* Franchise ID */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Franchise ID"
              name="franchise_id"
              type="number"
              value={formState.franchise_id || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.franchise_id}
              helperText={errorState.franchise_id ? 'Required' : ''}
            />
          </Grid>

          {/* Site Type */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.site_type}
              value={formState.site_type || ''}
              onChange={(e, value) => handleAutocompleteChange('site_type', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Site Type"
                  fullWidth
                  error={!!errorState.site_type}
                  helperText={errorState.site_type ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* Sharing Type (Host/Guest) */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.sharing_type_hostguest}
              value={formState.sharing_type_hostguest || ''}
              onChange={(e, value) => handleAutocompleteChange('sharing_type_hostguest', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sharing Type (Host/Guest)"
                  fullWidth
                  error={!!errorState.sharing_type_hostguest}
                  helperText={errorState.sharing_type_hostguest ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* OMO Name */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.omo_name}
              value={formState.omo_name || ''}
              onChange={(e, value) => handleAutocompleteChange('omo_name', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="OMO Name"
                  fullWidth
                  error={!!errorState.omo_name}
                  helperText={errorState.omo_name ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* B2S ID */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.b2s_id}
              value={formState.b2s_id || ''}
              onChange={(e, value) => handleAutocompleteChange('b2s_id', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="B2S ID"
                  fullWidth
                  error={!!errorState.b2s_id}
                  helperText={errorState.b2s_id ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* BSC */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.bsc}
              value={formState.bsc || ''}
              onChange={(e, value) => handleAutocompleteChange('bsc', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="BSC"
                  fullWidth
                  error={!!errorState.bsc}
                  helperText={errorState.bsc ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* BSC Code */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.bsc_code}
              value={formState.bsc_code || ''}
              onChange={(e, value) => handleAutocompleteChange('bsc_code', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="BSC Code"
                  fullWidth
                  error={!!errorState.bsc_code}
                  helperText={errorState.bsc_code ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* LTE Technology */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.lte_technology}
              value={formState.lte_technology || ''}
              onChange={(e, value) => handleAutocompleteChange('lte_technology', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="LTE Technology"
                  fullWidth
                  error={!!errorState.lte_technology}
                  helperText={errorState.lte_technology ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>

          {/* Deodar Tag */}
          <Grid item xs={4}>
            <Autocomplete
              options={options.deodar_tag}
              value={formState.deodar_tag || ''}
              onChange={(e, value) => handleAutocompleteChange('deodar_tag', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Deodar Tag"
                  fullWidth
                  error={!!errorState.deodar_tag}
                  helperText={errorState.deodar_tag ? 'Required' : ''}
                />
              )}
              size="small"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="2G Cells"
              name="2g_cells"
              type="number"
              value={formState['2g_cells'] || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState['2g_cells']}
              helperText={errorState['2g_cells'] ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="4G Cells"
              name="4g_cells"
              type="number"
              value={formState['4g_cells'] || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState['4g_cells']}
              helperText={errorState['4g_cells'] ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="L1800A Cells"
              name="l1800a_cells"
              type="number"
              value={formState.l1800a_cells || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.l1800a_cells}
              helperText={errorState.l1800a_cells ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="L1800B Cells"
              name="l1800b_cells"
              type="number"
              value={formState.l1800b_cells || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.l1800b_cells}
              helperText={errorState.l1800b_cells ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="MM Cells"
              name="mmcells"
              type="number"
              value={formState.mmcells || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.mmcells}
              helperText={errorState.mmcells ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="Total L18 (A&B) Cells"
              name="total_l18_ab_cells"
              type="number"
              value={formState.total_l18_ab_cells || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.total_l18_ab_cells}
              helperText={errorState.total_l18_ab_cells ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="L2100 Cells"
              name="l2100_cells"
              type="number"
              value={formState.l2100_cells || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.l2100_cells}
              helperText={errorState.l2100_cells ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="L900A Cells"
              name="l900a_cells"
              type="number"
              value={formState.l900a_cells || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.l900a_cells}
              helperText={errorState.l900a_cells ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="Total TRX"
              name="total_trx"
              type="number"
              value={formState.total_trx || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.total_trx}
              helperText={errorState.total_trx ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="900 TRX"
              name="900_trx"
              type="number"
              value={formState['900_trx'] || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState['900_trx']}
              helperText={errorState['900_trx'] ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="1800 TRX"
              name="1800_trx"
              type="number"
              value={formState['1800_trx'] || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState['1800_trx']}
              helperText={errorState['1800_trx'] ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="Franchise ID"
              name="franchise_id"
              type="number"
              value={formState.franchise_id || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.franchise_id}
              helperText={errorState.franchise_id ? 'Required' : ''}
            />
          </Grid>

          {/* Additional text fields */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="2G"
              name="2g"
              value={formState['2g'] || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState['2g']}
              helperText={errorState['2g'] ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="4G"
              name="4g"
              value={formState['4g'] || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState['4g']}
              helperText={errorState['4g'] ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Part of DEC CFO"
              name="part_of_dec_cfo"
              value={formState.part_of_dec_cfo || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.part_of_dec_cfo}
              helperText={errorState.part_of_dec_cfo ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Scope"
              name="scope"
              value={formState.scope || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.scope}
              helperText={errorState.scope ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="PTA Province"
              name="pta_province"
              value={formState.pta_province || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.pta_province}
              helperText={errorState.pta_province ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Prefix"
              name="prefix"
              value={formState.prefix || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.prefix}
              helperText={errorState.prefix ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="City (prefix-based)"
              name="city_prefix_based"
              value={formState.city_prefix_based || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.city_prefix_based}
              helperText={errorState.city_prefix_based ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Technical City"
              name="technical_city"
              value={formState.technical_city || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.technical_city}
              helperText={errorState.technical_city ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Site Type"
              name="site_type"
              value={formState.site_type || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.site_type}
              helperText={errorState.site_type ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Sharing Type (Host/Guest)"
              name="sharing_type_hostguest"
              value={formState.sharing_type_hostguest || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.sharing_type_hostguest}
              helperText={errorState.sharing_type_hostguest ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="OMO NAME"
              name="omo_name"
              value={formState.omo_name || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.omo_name}
              helperText={errorState.omo_name ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="B2S ID"
              name="b2s_id"
              value={formState.b2s_id || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.b2s_id}
              helperText={errorState.b2s_id ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="BSC"
              name="bsc"
              value={formState.bsc || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.bsc}
              helperText={errorState.bsc ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="BSC CODE"
              name="bsc_code"
              value={formState.bsc_code || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState.bsc_code}
              helperText={errorState.bsc_code ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="4G Capacity"
              name="4g_capacity"
              value={formState['4g_capacity'] || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState['4g_capacity']}
              helperText={errorState['4g_capacity'] ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="2G Locked Tag"
              name="2g_locked_tag"
              value={formState['2g_locked_tag'] || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState['2g_locked_tag']}
              helperText={errorState['2g_locked_tag'] ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="4G Locked Tag"
              name="4g_locked_tag"
              value={formState['4g_locked_tag'] || ''}
              onChange={handleChange}
              size="small"
              error={!!errorState['4g_locked_tag']}
              helperText={errorState['4g_locked_tag'] ? 'Required' : ''}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="ONFP Planning Remarks"
              name="onfp_remarks"
              value={formState.onfp_remarks}
              onChange={handleChange}
              multiline
              rows={3}
              size="small"
              error={!!errorState.onfp_remarks}
              helperText={errorState.onfp_remarks ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="BP Remarks"
              name="bp_remarks"
              value={formState.bp_remarks}
              onChange={handleChange}
              multiline
              rows={3}
              size="small"
              error={!!errorState.bp_remarks}
              helperText={errorState.bp_remarks ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Comments"
              name="comments"
              value={formState.comments}
              onChange={handleChange}
              multiline
              rows={3}
              error={!!errorState.comments}
              helperText={errorState.comments ? 'Required' : ''}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent={'end'} gap={2}>
              <Button
                variant="outlined"
                onClick={handleClear}
                sx={{
                  borderColor: '#2B323B',
                  color: '#2B323B',
                }}
              >
                Clear
              </Button>

              <Button variant="contained" style={{ background: '#BD1D23' }} onClick={handleSubmit}>
                Save
              </Button>
            </Box>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default RanDBUploadFormComponent;
